import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'
import { BCTC_PERIOD_RE } from '../bctc.ts'

/**
 * ══ HỢP ĐỒNG API — DANH MỤC KẾ TOÁN và KHOÁ SỔ ═══════════════════════════════════════════════
 *
 * ⚠ `AccCatalogBody` cố ý dùng `passthrough()`, KHÁC mọi lược đồ khác trong thư mục này. Danh mục
 *   kế toán là **bảng cấu hình động**: mỗi `kind` (tài khoản · ngân hàng · khoản mục chi phí · sổ
 *   định khoản…) có tập cột riêng, khai trong `TABLES` của `@rs/accounting` và được kiểm ở đó theo
 *   đúng `kind` của lời gọi. Liệt kê cứng mọi cột của mọi kind ở biên là dựng bản sao thứ hai của
 *   bảng khai đó — hai bản chắc chắn lệch nhau, và bản ở biên sẽ lặng lẽ CẮT MẤT cột mới thêm.
 *   Đây là ngoại lệ CÓ LÝ DO, không phải chỗ được nới cho tiện.
 */
export const AccCatalogBody = z.object({ version: Version.optional() }).passthrough()

export const AccKindParams = z.object({ kind: z.string() })
export const ListAccQuery = PageQuery
export type ListAccQueryT = z.infer<typeof PageQuery>
export const AccItemParams = z.object({ kind: z.string(), id: Uuid })
/** Xoá HẲN một ngân hàng khỏi Danh mục ngân hàng — bắt buộc `version` (chống xoá nhầm dòng vừa bị sửa). */
export const BankIdParams = z.object({ id: Uuid })
export const DeleteBankBody = z.object({ version: Version })
export type DeleteBankBodyT = z.infer<typeof DeleteBankBody>

export const SetLedgerLockBody = z.object({
  lockDate: blankAsAbsent(ISODate),
  note: z.string().optional(),
})

/** Mở khoá không cần dữ liệu gì — nhưng vẫn phải khai lược đồ, để cổng §7.1 không có ngoại lệ ngầm. */
export const ClearLedgerLockBody = z.object({}).passthrough()

/* ══ BÁO CÁO TÀI CHÍNH NHẬP TỪ EXCEL (tab 📑 BCTC) ═════════════════════════════════════════════ */

export const BctcYearQuery = z.object({
  year: z.coerce.number({ invalid_type_error: 'Năm phải là số.' }).int().min(2000).max(2100),
})

/**
 * ⚠ `values` LÀ CHUỖI THẬP PHÂN cho từng mã, không phải `number`. `number` của JSON là float đôi,
 *   và một báo cáo tài chính không được phép lệch một đồng vì chuyện đó (§2).
 */
export const SetBctcPeriodBody = z.object({
  /** Tháng · quý · 6T · 9T · năm — xem `BCTC_PERIOD_RE` (bản cũ `bctcKey`, V03548/V03582). */
  period: z.string().regex(BCTC_PERIOD_RE, 'Kỳ phải có dạng YYYY-MM, YYYY-Qn, YYYY-6T, YYYY-9T hoặc YYYY-NAM.'),
  values: z.record(z.string(), z.string()),
  fileName: blankAsAbsent(z.string()),
})

export const SetBctcTargetBody = z.object({
  year: z.number().int().min(2000).max(2100),
  code: z.string().min(1, 'Thiếu mã chỉ tiêu.'),
  targetValue: moneyField('Kế hoạch năm'),
})

export const BctcIdParams = z.object({ id: Uuid })
/** Cửa xoá KHÔNG có thân — nhưng `POST` vẫn khai lược đồ, để thân lạ bị từ chối. */
export const BctcNoBody = z.object({}).strict()

export type BctcYearQueryT = z.infer<typeof BctcYearQuery>
export type SetBctcPeriodBodyT = z.infer<typeof SetBctcPeriodBody>
export type SetBctcTargetBodyT = z.infer<typeof SetBctcTargetBody>

/* ══ ĐỊNH KHOẢN ĐÃ NHỚ THEO NCC (`0068`) ═════════════════════════════════════════════════════ */

/**
 * ⚠ Luật "phải điền ít nhất một ô" chốt ở `@rs/accounting` để câu từ chối là tiếng Việt và nằm
 *   cùng chỗ với `shouldLearn` — khai lần hai ở đây là hai bản luật cho một câu hỏi.
 */
export const SaveVendorPostingBody = z.object({
  vendorId: Uuid,
  debitAcc: blankAsAbsent(z.string()),
  creditAcc: blankAsAbsent(z.string()),
  vatAcc: blankAsAbsent(z.string()),
  vatRate: z.number().min(0, 'Thuế suất phải từ 0 đến 100.')
    .max(100, 'Thuế suất phải từ 0 đến 100.').optional(),
  itemCode: blankAsAbsent(z.string()),
  pinned: z.boolean().optional(),
})

export const DeleteVendorPostingBody = z.object({ version: Version })

/** Không bộ lọc nào — bảng nhỏ (một dòng mỗi NCC) và màn cần cả bảng để dò lúc dựng chứng từ. */
export const VendorPostingQuery = z.object({ vendorId: blankAsAbsent(Uuid) })

export const VendorPostingIdParams = z.object({ id: Uuid })

export type SaveVendorPostingBodyT = z.infer<typeof SaveVendorPostingBody>
export type DeleteVendorPostingBodyT = z.infer<typeof DeleteVendorPostingBody>
export type VendorPostingQueryT = z.infer<typeof VendorPostingQuery>

/* ══ NHẬT KÝ ĐỒNG BỘ (`0074`) ════════════════════════════════════════════════════════════════ */

/** ⚠ CHỈ CÓ CỬA ĐỌC. Nhật ký do chính đường đẩy tự ghi — một cửa "thêm dòng" là mời viết lại sử. */
export const SyncLogQuery = z.object({
  limit: z.coerce.number().int().min(1).max(500).optional(),
})

export type SyncLogQueryT = z.infer<typeof SyncLogQuery>
